import { computed, watch } from "vue";

import { useGetOfferItemsQuery } from "@/graphql";
import useFeatureFlags from "@/hooks/useFeatureFlags";
import useCampaigns from "@/modules/Offers/hooks/useCampaigns";

export interface TreatmentOfferSummary {
  id: string;
  name: string;
  abtestLinkMode: string | null;
  campaignId: string | null;
}

/**
 * Page-level data source for the cohort treatment offers display. CohortDetails/TreatmentOffersDisplay
 * render one instance per cohort × treatment, so fetching offers and campaigns in those components fired
 * dozens of concurrent requests (and as many reactive regroupings) on cohort-heavy pages. Call this once
 * per page and pass the lookup records down as props instead.
 */
export default function useTreatmentOffersData(gameId: string, versionId: string) {
  const { flags } = useFeatureFlags(gameId);

  const { result: offersResult } = useGetOfferItemsQuery(
    { appId: gameId, versionId },
    { fetchPolicy: "cache-and-network" }
  );

  const offersById = computed<Record<string, TreatmentOfferSummary>>(() => {
    const byId: Record<string, TreatmentOfferSummary> = {};
    offersResult.value?.getOfferItems?.forEach((offer) => {
      if (offer?.id) {
        byId[offer.id] = {
          id: offer.id,
          name: offer.name || offer.id,
          abtestLinkMode: offer.abtestLinkMode ?? null,
          campaignId: offer.campaignId ?? null,
        };
      }
    });
    return byId;
  });

  // Campaign names only appear in the grouped (multi-link) display, so skip the request when the flag
  // is off for this game. Watched (once) rather than checked inline because the flag may resolve after
  // setup, via useFeatureFlags' route watcher.
  // The flag is sticky (false -> true once), so this fires getCampaigns at most once.
  const { campaigns, getCampaigns } = useCampaigns(gameId, versionId);
  watch(
    () => flags.value.offerMultiLinkABTest.enabled,
    (enabled) => {
      if (enabled) {
        getCampaigns();
      }
    },
    { immediate: true }
  );

  const campaignNamesById = computed<Record<string, string>>(() =>
    Object.fromEntries(campaigns.value.map((campaign) => [campaign.id, campaign.name]))
  );

  return { offersById, campaignNamesById };
}
